import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Logo";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Newspaper, ExternalLink, Radio, TrendingUp, Loader2, X, Sparkles } from "lucide-react";

const THUMBS = {
  Culture: "https://images.pexels.com/photos/38942536/pexels-photo-38942536.jpeg",
  Community: "https://images.pexels.com/photos/13801809/pexels-photo-13801809.jpeg",
  Business: "https://images.unsplash.com/photo-1783538830797-82fa41e145dc",
  Finance: "https://images.unsplash.com/photo-1783538830797-82fa41e145dc",
  Health: "https://images.pexels.com/photos/5163857/pexels-photo-5163857.jpeg",
  Education: "https://images.pexels.com/photos/38942536/pexels-photo-38942536.jpeg",
  "Black history & culture": "https://images.pexels.com/photos/38942536/pexels-photo-38942536.jpeg",
  "Health & wellness": "https://images.pexels.com/photos/5163857/pexels-photo-5163857.jpeg",
  "Personal finance & wealth building": "https://images.unsplash.com/photo-1783538830797-82fa41e145dc",
  "Business & entrepreneurship": "https://images.unsplash.com/photo-1783538830797-82fa41e145dc",
  "Community & current affairs": "https://images.pexels.com/photos/13801809/pexels-photo-13801809.jpeg",
};
const FALLBACKS = Object.values(THUMBS);

function thumbFor(item, i) {
  return THUMBS[item.category] || FALLBACKS[i % FALLBACKS.length];
}

export default function FeedView() {
  const { isPro } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [live, setLive] = useState(false);
  const [trending, setTrending] = useState([]);
  const [reader, setReader] = useState(null); // { item, idx }
  const [readerBody, setReaderBody] = useState("");
  const [readerLoading, setReaderLoading] = useState(false);

  const load = (refresh = false) => {
    setLoading(true);
    api.get(`/feed${refresh ? "?refresh=true" : ""}`)
      .then((r) => { setItems(r.data.items); setDate(r.data.date); setLive(!!r.data.live); setTrending(r.data.trending || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openReader = (item, idx) => {
    setReader({ item, idx });
    setReaderBody("");
    setReaderLoading(true);
    api.post("/feed/expand", { title: item.title, summary: item.summary, url: item.url, category: item.category })
      .then((r) => setReaderBody(r.data.body))
      .catch(() => setReaderBody("We couldn't load this briefing right now. Please try again in a moment."))
      .finally(() => setReaderLoading(false));
  };

  return (
    <div className="h-full overflow-y-auto px-4 sm:px-8 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-2">
              <Newspaper size={14} /> Daily Briefing
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none flex items-center gap-3">
              Today's Feed
              {live && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#FF5A00] text-[#0a0a0f] px-2 py-1 rounded-full font-bold">
                  <Radio size={11} /> Live
                </span>
              )}
            </h1>
            {date && <p className="text-[#9ca3af] text-sm mt-1">{new Date(date).toDateString()}</p>}
          </div>
          <button data-testid="feed-refresh-button" onClick={() => load(true)}
            className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {!loading && trending.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6" data-testid="trending-strip">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
              <TrendingUp size={14} /> Trending in the Culture
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "thin" }}>
              {trending.map((t, i) => (
                <button key={i} data-testid={`trending-chip-${i}`} className="shrink-0"
                  onClick={() => openReader({ title: t.label, url: t.url, category: "Trending in the Culture", summary: "" }, i)}>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap bg-[#1a1a24] border border-[#2a3040] text-white text-sm font-medium rounded-full px-4 py-2 hover:border-[#FF5A00] hover:-translate-y-[2px] transition-all">
                    <span className="text-[#FF5A00] font-bold">#{i + 1}</span> {t.label}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-28 bg-[#15151d] border border-[#2a3040] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((it, i) => (
              <div key={i} className="contents">
              <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }} data-testid={`feed-item-${i}`}>
                <button onClick={() => openReader(it, i)} className="block w-full text-left"
                  data-testid={`feed-open-${i}`}>
                  <div className="flex gap-4 bg-[#15151d] border border-[#2a3040] rounded-lg overflow-hidden hover:border-[#FF5A00] hover:-translate-y-[2px] transition-all">
                    <div className="w-28 sm:w-40 shrink-0 overflow-hidden">
                      <img src={thumbFor(it, i)} alt="" className="w-full h-full object-cover min-h-[112px]" />
                    </div>
                    <div className="py-4 pr-4 flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] font-bold">{it.category}</span>
                        {it.source && <span className="text-[10px] text-[#6b7280] truncate">· {it.source}</span>}
                      </div>
                      <h3 className="font-display font-bold text-lg leading-tight text-white mb-1.5">
                        <span className="line-clamp-2">{it.title}</span>
                      </h3>
                      <p className="text-sm text-[#9ca3af] leading-relaxed line-clamp-2">{it.summary}</p>
                    </div>
                  </div>
                </button>
              </motion.article>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!reader} onOpenChange={(o) => !o && setReader(null)}>
        <DialogContent className="bg-[#0f0f16] border-[#2a3040] text-white max-w-2xl max-h-[85vh] overflow-y-auto p-0 [&>button]:hidden"
          data-testid="feed-reader">
          {reader && (
            <div className="relative">
              <DialogTitle className="sr-only">{reader.item.title}</DialogTitle>
              <button data-testid="feed-reader-close" onClick={() => setReader(null)} aria-label="Close"
                className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 backdrop-blur text-white font-black text-xl leading-none hover:bg-[#FF5A00] hover:text-[#0a0a0f] transition-colors">
                X
              </button>
              <div className="relative h-44 sm:h-56 overflow-hidden">
                <img src={thumbFor(reader.item, reader.idx)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f16] via-[#0f0f16]/40 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] font-bold">{reader.item.category}</span>
                    {reader.item.source && <span className="text-[10px] text-[#9ca3af]">· {reader.item.source}</span>}
                  </div>
                  <h2 className="font-display font-black text-2xl sm:text-3xl leading-tight text-white">{reader.item.title}</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
                  <Sparkles size={13} /> The <Brand /> Briefing
                </div>
                {readerLoading ? (
                  <div className="flex items-center gap-2 text-[#9ca3af] py-6">
                    <Loader2 size={18} className="animate-spin text-[#FF5A00]" /> Writing your briefing…
                  </div>
                ) : (
                  <div className="text-[#d1d5db] leading-relaxed space-y-4" data-testid="feed-reader-body">
                    {readerBody.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                )}

                {reader.item.url && (
                  <a href={reader.item.url} target="_blank" rel="noreferrer" data-testid="feed-reader-source"
                    className="inline-flex items-center gap-1.5 mt-6 text-sm text-[#9ca3af] hover:text-[#FF5A00] transition-colors border-t border-[#2a3040] pt-4 w-full">
                    Read the original source at {reader.item.source || "the publisher"} <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
